import { useEffect, useMemo, useState } from 'react';

/** ISO-ish country key from region code (lv, ru2 → lv/ru). */
export function regionFlagKey(code: string): string {
  const letters = code.trim().toLowerCase().replace(/[^a-z]/g, '');
  return letters.slice(0, 2) || code.trim().toLowerCase();
}

const FLAG_EXTS = ['png', 'webp', 'jpg', 'jpeg', 'svg'] as const;

function flagUrl(key: string, ext: string): string {
  const base = import.meta.env.BASE_URL || '/';
  const root = base.endsWith('/') ? base : `${base}/`;
  return `${root}flags/${key}.${ext}`;
}

interface RegionFlagProps {
  code: string;
  label?: string;
  size?: number;
}

/**
 * Circular country flag from `{BASE_URL}flags/{cc}.png|webp|jpg|svg`.
 * Portal is served under /user/, so paths must use Vite BASE_URL.
 */
export function RegionFlag({ code, label, size = 36 }: RegionFlagProps) {
  const key = useMemo(() => regionFlagKey(code), [code]);
  const [extIndex, setExtIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setExtIndex(0);
    setFailed(false);
  }, [key]);

  if (failed || extIndex >= FLAG_EXTS.length) {
    return (
      <span
        className="region-flag region-flag-fallback"
        style={{ width: size, height: size }}
        title={label || code}
      >
        {code.trim().toUpperCase().slice(0, 3) || '—'}
      </span>
    );
  }

  const ext = FLAG_EXTS[extIndex]!;
  const src = flagUrl(key, ext);

  return (
    <span className="region-flag" style={{ width: size, height: size }}>
      <img
        src={src}
        alt={label || code.toUpperCase()}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        onError={() => {
          const next = extIndex + 1;
          if (next >= FLAG_EXTS.length) setFailed(true);
          else setExtIndex(next);
        }}
      />
    </span>
  );
}
